import { useTranslation } from "react-i18next";
import { Input } from "../../../components/ui/Input";
import { registryPresentation } from "../registryPresentation";

/**
 * The desktop registry slide when the name matched nobody (Figma
 * "D7.identite.rpps-introuvable"), for the registries searched by name and
 * then by number: the number, or a second look at the name.
 *
 * The screen in the design also offers to verify with a professional
 * document. Nothing verifies a document today, so the link is not drawn: the
 * way on without a number is the footer's manual fill-in.
 */
export function RegistryNotFound({
  registry,
  registryId,
  onRegistryIdChange,
  onSubmitRegistryId,
  onEditName,
}: {
  registry: Exclude<keyof typeof registryPresentation, "reps" | "stps" | "big">;
  registryId: string;
  onRegistryIdChange: (registryId: string) => void;
  onSubmitRegistryId: () => void;
  /** Back to the details screen, where the name was typed. */
  onEditName: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { identifierLabel } = registryPresentation[registry];
  if (identifierLabel === null)
    throw new Error(`Invariant: ${registry} is searched by number`);

  return (
    <form
      className="flex w-full flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (registryId.trim().length > 0) onSubmitRegistryId();
      }}
    >
      <label className="flex w-full flex-col gap-2">
        <span className="font-dm-sans text-sm font-medium text-ds-slate-950">
          {t("{{identifier}} number", { identifier: identifierLabel })}
        </span>
        <Input
          variant="ghost"
          className="rounded-xl border-ds-slate-200 bg-ds-white focus-within:border-ds-slate-500"
          inputMode="numeric"
          autoFocus
          placeholder={
            registry === "rpps"
              ? t("11 digits, printed on your CPS card")
              : t("Enter your registry ID")
          }
          value={registryId}
          onChange={(e) => {
            onRegistryIdChange(e.target.value);
          }}
        />
      </label>
      <button
        type="button"
        className="self-start border-none bg-transparent p-0 font-dm-sans text-sm font-medium text-ds-mint-800 underline"
        onClick={onEditName}
      >
        {t("Edit how my name is spelled")}
      </button>
    </form>
  );
}
